import React, { useMemo, useEffect, useState } from 'react';
import { useDashboard } from '../../context/DashboardContext';
import { useConfiguration } from '../../context/ConfigurationContext';
import { calculateIndicatorStatusBreakdown, getIndicatorStatus, fetchIndicatorStatus, clearIndicatorStatusCache } from '../../utils/priorityUtils';
import IndicatorTag from './IndicatorTag';
import StatusBreakdown from './StatusBreakdown';

const ImpactCard = ({ impact, city, isDataAvailable, isExpanded = false, onToggle, rank }) => {
  const { selectImpact, setTargetIndicator } = useDashboard();
  const { hasIndicatorData } = useConfiguration();
  const [statusUpdateCounter, setStatusUpdateCounter] = useState(0);
  
  // Get rank color
  const getRankColor = (r) => {
    if (r === 1) return '#B71C1C';
    if (r === 2) return '#EF6C00';
    if (r === 3) return '#F9A825';
    if (r === 4) return '#2E7D32';
    return '#ccc';
  };
  const rankColor = rank ? getRankColor(rank) : null;

  // Listen for configuration changes
  useEffect(() => {
    const handleConfigurationChange = () => {
      // Force re-render when configuration changes
      setStatusUpdateCounter(prev => prev + 1);
    };
    
    window.addEventListener('configurationChanged', handleConfigurationChange);
    return () => window.removeEventListener('configurationChanged', handleConfigurationChange);
  }, []);

  // Fetch indicator statuses in background (non-blocking)
  useEffect(() => {
    if (!city || !impact) return;
    
    // Don't block UI - fetch statuses in background and update as they complete
    impact.sectors.forEach(sector => {
      sector.indicators.forEach(ind => {
        if (hasIndicatorData(city, ind.name)) {
          // Fetch each status independently without blocking
          fetchIndicatorStatus(ind.name, city)
            .then(() => {
              // Trigger re-render when status is loaded
              setStatusUpdateCounter(prev => prev + 1);
            })
            .catch(err => {
              console.warn(`Failed to fetch status for ${ind.name}:`, err);
              // Don't block UI on errors
            });
        }
      });
    });
  }, [city, impact, hasIndicatorData]);

  // Clear cache and refetch when thresholds change (non-blocking)
  useEffect(() => {
    const handleThresholdChange = () => {
      if (city && impact) {
        clearIndicatorStatusCache();
        // Refetch statuses in background without blocking
        impact.sectors.forEach(sector => {
          sector.indicators.forEach(ind => {
            if (hasIndicatorData(city, ind.name)) {
              fetchIndicatorStatus(ind.name, city)
                .then(() => {
                  setStatusUpdateCounter(prev => prev + 1); // Increment to trigger update
                })
                .catch(err => {
                  console.warn(`Failed to refetch status for ${ind.name}:`, err);
                });
            }
          });
        });
      }
    };

    window.addEventListener('thresholdChanged', handleThresholdChange);
    return () => window.removeEventListener('thresholdChanged', handleThresholdChange);
  }, [city, impact]);

  // Calculate indicator information - Show all indicators, with lock icon for those without data
  const indicatorInfo = useMemo(() => {
    const allIndicatorsInfo = [];
    
    impact.sectors.forEach(sector => {
      sector.indicators.forEach(ind => {
        const hasData = hasIndicatorData(city, ind.name);
        // Include all indicators, regardless of data availability
        const status = hasData ? getIndicatorStatus(ind.name, city) : null;
        allIndicatorsInfo.push({
          name: ind.name,
          hasData: hasData, // Set based on actual data availability
          sector: sector.name,
          threshold: ind.threshold,
          dataNeeded: ind.dataNeeded,
          status: status
        });
      });
    });

    return allIndicatorsInfo;
  }, [impact, city, statusUpdateCounter, hasIndicatorData]); // Include hasIndicatorData to react to configuration changes

  // Calculate status breakdown
  const statusBreakdown = useMemo(() => {
    return calculateIndicatorStatusBreakdown(impact, city, hasIndicatorData);
  }, [impact, city, statusUpdateCounter, hasIndicatorData]); // Include hasIndicatorData to react to configuration changes

  // Handle indicator tag click
  const handleIndicatorClick = (indInfo) => {
    if (!indInfo.hasData) return;
    
    // Set target indicator before selecting impact
    setTargetIndicator({
      name: indInfo.name,
      sector: indInfo.sector,
      threshold: indInfo.threshold,
      dataNeeded: indInfo.dataNeeded
    });
    
    selectImpact(impact.id, city);
  };

  const getBorderColor = () => {
    if (statusBreakdown.cardBorderColor === 'high') return '#c0392b';
    if (statusBreakdown.cardBorderColor === 'warning') return '#f39c12';
    if (statusBreakdown.cardBorderColor === 'normal') return '#27ae60';
    return '#ccc';
  };

  return (
    <div className={`impact-card status-${statusBreakdown.cardBorderColor}`} style={{ 
      padding: 0, 
      borderLeft: 'none',
      border: '3px solid #b0b0b0',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      overflow: 'hidden'
    }}>
      {/* Expandable Ribbon Header */}
      <div
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          padding: '0.75rem 1rem',
          borderBottom: '1px solid #e9ecef',
          cursor: 'pointer',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          transition: 'background 0.2s ease',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px'
        }}
        onClick={onToggle}
        onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'}
      >
        <div style={{
          fontSize: '0.95rem',
          fontWeight: 700,
          color: rankColor || 'var(--primary)',
          letterSpacing: '0.2px',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          flex: 1
        }}>
          {impact.title}
          <span style={{
            fontSize: '0.7rem',
            color: 'var(--primary)',
            transition: 'transform 0.3s ease',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            display: 'inline-block'
          }}>
            ▶
          </span>
        </div>
      </div>

      {/* Collapsible Content */}
      {isExpanded && (
        <div style={{
          padding: '1rem',
          animation: 'slideDown 0.3s ease-out',
          backgroundColor: '#ffffff'
        }}>
          {/* Indicator Tags Section */}
      <div style={{
        marginTop: '0',
        padding: '0.5rem 0',
        marginBottom: '1rem'
      }}>
        {/* Header */}
        <div style={{
          fontSize: '0.8rem',
          fontWeight: 700,
          color: 'var(--primary)',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          marginBottom: '0.4rem'
        }}>
          Indicators
        </div>

        {/* Tags wrapper - matching IndicatorTabs grid layout */}
        <div className="indicator-tags-grid" style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: '0.5rem'
        }}>
          {indicatorInfo.map((indInfo, index) => (
            <IndicatorTag
              key={`${indInfo.name}-${index}`}
              indicator={indInfo}
              onClick={() => handleIndicatorClick(indInfo)}
            />
          ))}
        </div>
      </div>

          {/* Status Breakdown */}
          <div style={{
            padding: '0.5rem 0'
          }}>
            <StatusBreakdown breakdown={statusBreakdown} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImpactCard;

